import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const CombinedCropCoefficient = ({ combinedData = null, showTitle = true }) => {
  const [chartData, setChartData] = useState([]);
  const [cropLines, setCropLines] = useState([]);

  const generateCropColor = (index) => {
    const hue = (index * 137.5) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  const formatNumber = (num) => {
    if (typeof num !== "number") return "0";
    return num.toFixed(2);
  };

  useEffect(() => {
    if (
      !combinedData ||
      !combinedData.crops ||
      combinedData.crops.length === 0
    ) {
      setChartData([]);
      setCropLines([]);
      return;
    }

    try {
      const allCrops = combinedData.crops;
      const cropLinesData = [];
      let maxTotalDays = 0;

      allCrops.forEach((cropResult, cropIndex) => {
        if (
          !cropResult.growth_stage_calculations ||
          !cropResult.growth_stage_calculations.growth_stages
        ) {
          return;
        }

        const cropName =
          cropResult.crop_input.crop_type.charAt(0).toUpperCase() +
          cropResult.crop_input.crop_type.slice(1);
        const cropColor = generateCropColor(cropIndex);
        const stages = cropResult.growth_stage_calculations.growth_stages;

        const initialDays = stages.initial?.days || 30;
        const devDays = stages.development?.days || 30;
        const midDays = stages.mid_season?.days || 80;
        const lateDays = stages.late_season?.days || 30;

        const initialKc = stages.initial?.coefficient || 0.37;
        const midKc = stages.mid_season?.coefficient || 1.1;
        const lateKc = stages.late_season?.coefficient || 0.43;

        const initialEnd = initialDays;
        const devEnd = initialEnd + devDays;
        const midEnd = devEnd + midDays;
        const totalDays = initialDays + devDays + midDays + lateDays;

        maxTotalDays = Math.max(maxTotalDays, totalDays);

        const curvePoints = [];

        const initialPoints = Math.max(Math.floor(initialDays / 2), 5);
        for (let i = 0; i <= initialPoints; i++) {
          curvePoints.push({
            day: (initialEnd * i) / initialPoints,
            [cropName]: initialKc,
            stage: "initial",
          });
        }

        const devPoints = Math.max(Math.floor(devDays / 2), 5);
        for (let i = 1; i <= devPoints; i++) {
          const ratio = i / devPoints;
          curvePoints.push({
            day: initialEnd + devDays * ratio,
            [cropName]: initialKc + (midKc - initialKc) * ratio,
            stage: "development",
          });
        }

        const midPoints = Math.max(Math.floor(midDays / 3), 5);
        for (let i = 1; i <= midPoints; i++) {
          curvePoints.push({
            day: devEnd + (midDays * i) / midPoints,
            [cropName]: midKc,
            stage: "mid-season",
          });
        }

        const latePoints = Math.max(Math.floor(lateDays / 2), 5);
        for (let i = 1; i <= latePoints; i++) {
          const ratio = i / latePoints;
          curvePoints.push({
            day: midEnd + lateDays * ratio,
            [cropName]: midKc + (lateKc - midKc) * ratio,
            stage: "late-season",
          });
        }

        cropLinesData.push({
          name: cropName,
          color: cropColor,
          dataKey: cropName,
          points: curvePoints,
          area: cropResult.crop_input.area,
          plantingDate: cropResult.crop_input.planting_date,
          totalDays,
          stages: {
            initialEnd,
            devEnd,
            midEnd,
            totalDays,
          },
        });
      });

      const combinedPoints = [];
      const dayStep = 1;

      for (let day = 0; day <= maxTotalDays; day += dayStep) {
        const dayData = { day };

        cropLinesData.forEach((crop) => {

          const closestPoint = crop.points.reduce((prev, curr) => {
            return Math.abs(curr.day - day) < Math.abs(prev.day - day)
              ? curr
              : prev;
          });

          if (closestPoint && Math.abs(closestPoint.day - day) <= dayStep * 2) {
            dayData[crop.dataKey] = closestPoint[crop.dataKey];
          }
        });

        combinedPoints.push(dayData);
      }

      setChartData(combinedPoints);
      setCropLines(cropLinesData);
    } catch (error) {
      console.error("Error processing combined crop coefficient data:", error);
    }
  }, [combinedData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "2px solid #ccc",
            padding: "12px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            maxWidth: "280px",
          }}
        >
          <p style={{ margin: "0 0 8px", fontWeight: "bold", color: "#333" }}>
            📅 Day {Math.round(label)} after planting
          </p>
          {payload.map((entry, index) => {
            const cropInfo = cropLines.find(
              (crop) => crop.dataKey === entry.dataKey
            );
            return (
              <p
                key={index}
                style={{
                  margin: "0 0 4px",
                  fontSize: "12px",
                  color: entry.color,
                }}
              >
                🌱 <strong>{entry.dataKey}</strong>: Kc ={" "}
                {formatNumber(entry.value || 0)}
                {cropInfo && (
                  <span style={{ fontSize: "11px", color: "#666" }}>
                    {" "}
                    ({cropInfo.area} acres)
                  </span>
                )}
              </p>
            );
          })}
        </div>
      );
    }
    return null;
  };

  if (!combinedData || !combinedData.crops || combinedData.crops.length === 0) {
    return (
      <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
        {showTitle && <h3>Combined Crop Coefficient Curves</h3>}
        <p>
          No crop data available. Please submit crops to see coefficient curves.
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {showTitle && (
        <>
          <h3
            style={{
              textAlign: "center",
              marginBottom: "10px",
              color: "#333",
              fontSize: "20px",
            }}
          >
            🌾 Crop Water Requirement
          </h3>

          <div
            style={{
              textAlign: "center",
              marginBottom: "20px",
              color: "#666",
              fontSize: "14px",
            }}
          >
            <p style={{ margin: "0" }}>
              Comparing crop water requirement across their growth cycle
              cycles
            </p>
          </div>
        </>
      )}

      <div
        style={{
          border: "2px solid #e0e0e0",
          borderRadius: "12px",
          padding: "20px",
          backgroundColor: "#fafafa",
        }}
      >
        {}
        <div
          style={{
            marginTop: "20px",
            padding: "15px",
            backgroundColor: "#f0f8ff",
            borderRadius: "8px",
          }}
        >
          <h5
            style={{ margin: "0 0 10px", color: "#333", textAlign: "center" }}
          >
            📈 Growth Stage Patterns
          </h5>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "10px",
              fontSize: "12px",
              color: "#555",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <strong>🌱 Initial:</strong> Low water needs, establishing roots
            </div>
            <div style={{ textAlign: "center" }}>
              <strong>📈 Development:</strong> Increasing water needs, rapid
              growth
            </div>
            <div style={{ textAlign: "center" }}>
              <strong>🌾 Mid-season:</strong> Peak water needs,
              flowering/fruiting
            </div>
            <div style={{ textAlign: "center" }}>
              <strong>📉 Late-season:</strong> Decreasing needs, maturation
            </div>
          </div>
        </div>
        <div style={{ height: "400px", width: "100%" }}>
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />
              <XAxis
                dataKey="day"
                type="number"
                scale="linear"
                domain={["dataMin", "dataMax"]}
                tick={{ fontSize: 12, fill: "#666" }}
                label={{
                  value: "Days after planting",
                  position: "insideBottom",
                  offset: -10,
                  style: { textAnchor: "middle", fontSize: 14, fill: "#333" },
                }}
              />
              <YAxis
                domain={[0, 1.3]}
                tick={{ fontSize: 12, fill: "#666" }}
                label={{
                  value: "Crop Coefficient (Kc)",
                  angle: -90,
                  position: "insideLeft",
                  style: { textAnchor: "middle", fontSize: 14, fill: "#333" },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="line" />

              {}
              {cropLines.map((crop, index) => (
                <Line
                  key={crop.dataKey}
                  type="monotone"
                  dataKey={crop.dataKey}
                  stroke={crop.color}
                  strokeWidth={3}
                  dot={false}
                  activeDot={{
                    r: 6,
                    stroke: crop.color,
                    strokeWidth: 2,
                    fill: "white",
                  }}
                  name={`${crop.name} (${crop.area} acres)`}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CombinedCropCoefficient;
